import { BREAKPOINTS, COLORS, ComponentTextStyle, ComponentXL, ComponentXXS, LoadingIndicator, SystemIcons } from '@laerdal/life-react-components';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

interface SamaritanResponseProps {
  isStreaming: boolean;
  response: string;
  noResponse: boolean;
  cannotAnswer: boolean;
  question: string;
}

const ResponseContainer = styled.div`
  border-radius: 8px;
  border: 2px solid ${COLORS.text_samaritan};
  background: ${COLORS.white};
  padding: 12px 16px;
  font-family: Lato;
  font-size: 16px;
  line-height: 1.6;
  color: ${COLORS.neutral_800};
  min-height: 60px;

  ${BREAKPOINTS.MEDIUM} {
    padding: 24px 32px;
  }
`;

const LoadingIndicatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
`;

const SamaritanText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const SamaritanTextHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const SamaritanMarkdownContainer = styled.div`

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.5);
  }

  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;

  border-radius: 8px;
  padding: 24px;
  background: linear-gradient(to right, #FBDBFB, #E0E6FA);

  h1, h2, h3, h4 {
    margin: 16px 0 8px;
    &:first-child { margin-top: 0; }
  }
  h2 { font-size: 20px; }
  h3 { font-size: 18px; }

  p { margin: 0 0 12px; &:last-child { margin-bottom: 0; } }

  ul, ol {
    margin: 0 0 12px;
    padding-left: 24px;
  }
  li { margin-bottom: 4px; }

  a {
    color: ${COLORS.primary_500};
    text-decoration: underline;
    word-break: break-all;
  }

  strong { font-weight: 700; }

  hr { border: none; border-top: 1px solid ${COLORS.neutral_200}; margin: 16px 0; }

  p, li, ul, ol, label {
    font-family: Lato;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;

    ${BREAKPOINTS.MEDIUM} {
      font-size: 20px;
    }
  }
`;

const FeedbackContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 0 0;
`;

const FeedbackText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${COLORS.text_subtle};
`;

const FeedbackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  color: ${COLORS.neutral_600};

  &:hover {
    color: ${COLORS.neutral_800};
  }
`;

const SamaritanIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M14.1419 15.4528C15.1853 16.4667 16.8531 16.4575 17.8853 15.4254L18.8281 14.4825C20.3902 12.9205 22.9229 12.9205 24.485 14.4826C25.2095 15.2071 25.5979 16.1403 25.6504 17.0886L26.8422 15.8968C29.1854 13.5537 29.1854 9.75468 26.8422 7.41153C24.4991 5.06839 20.7001 5.06839 18.3569 7.41153L14.0834 11.6852C13.0818 12.7196 13.0829 14.3645 14.0866 15.3976L14.1419 15.4528Z" fill="#DBC6EC"/>
    <path d="M23.5246 15.4077L23.5603 15.4433C24.583 16.4852 24.578 18.1582 23.5453 19.1939L23.5425 19.1967L16.6713 26.0679C16.3006 26.4386 15.6995 26.4386 15.3288 26.0679L5.15775 15.8968C2.8146 13.5537 2.8146 9.75468 5.15775 7.41153C7.5009 5.06839 11.2999 5.06839 13.643 7.41153L15.0573 8.82575L13.1716 10.7113C11.6095 12.2734 11.6095 14.8061 13.1716 16.3682C14.7337 17.9303 17.2663 17.9303 18.8284 16.3682L19.7713 15.4254C20.8067 14.39 22.4818 14.3841 23.5246 15.4077Z" fill="#EFDFFC"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M16.0004 7.88294L17.4148 6.46868C20.2786 3.60484 24.9218 3.60484 27.7857 6.46868C30.6495 9.33253 30.6495 13.9757 27.7857 16.8396L24.5594 20.0659L24.4907 20.1344C24.489 20.1361 24.4873 20.1377 24.4857 20.1394L17.6144 27.0107C16.723 27.9021 15.2777 27.9021 14.3863 27.0107L4.21527 16.8396C1.35142 13.9757 1.35142 9.33253 4.21527 6.46868C7.07911 3.60484 11.7223 3.60484 14.5862 6.46868L16.0004 7.88294ZM16.6716 26.0678C16.3009 26.4386 15.6999 26.4386 15.3291 26.0678L5.15807 15.8968C2.81493 13.5536 2.81493 9.75464 5.15808 7.41149C7.50122 5.06835 11.3002 5.06835 13.6434 7.41149L15.0576 8.82571L13.1719 10.7113C11.6098 12.2734 11.6098 14.806 13.1719 16.3681C14.734 17.9302 17.2666 17.9302 18.8287 16.3681L19.7716 15.4254C20.813 14.384 22.5014 14.384 23.5428 15.4254C24.5833 16.4658 24.5842 18.1522 23.5457 19.1938C23.5447 19.1947 23.5438 19.1957 23.5428 19.1966L16.6716 26.0678ZM26.8429 15.8968L25.6511 17.0886C25.5986 16.1402 25.2101 15.207 24.4857 14.4826C22.9236 12.9205 20.3909 12.9204 18.8288 14.4825L17.8859 15.4253C16.8445 16.4667 15.1561 16.4667 14.1147 15.4253C13.0733 14.3839 13.0733 12.6955 14.1147 11.6541L18.3576 7.41149C20.7007 5.06835 24.4997 5.06835 26.8429 7.41149C29.186 9.75464 29.186 13.5536 26.8429 15.8968Z" fill="#1A1A1A"/>
  </svg>
);

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

const SamaritanResponse = ({ isStreaming, response, noResponse, cannotAnswer, question }: SamaritanResponseProps) => {
  const { t } = useTranslation("cases");
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  useEffect(() => {
    setFeedbackGiven(false);
  }, [response]);

  const handleFeedback = (positive: boolean) => {
    posthog?.capture?.("SUPP-Casedeflection-AiSummaryFeedback", {
      positive,
      question,
      response,
    });
    setFeedbackGiven(true);
  };

  return (
    <>
    <ResponseContainer>
      {isStreaming && !response && (
        <LoadingIndicatorContainer>
          <LoadingIndicator />
        </LoadingIndicatorContainer>
      )}

      {(noResponse || cannotAnswer) && (
        <SamaritanText>
          <SamaritanTextHeader>
            <SamaritanIcon />
            <ComponentXL textStyle={ComponentTextStyle.Bold}>{t("AI summarized answer")}</ComponentXL>
          </SamaritanTextHeader>
          <SamaritanMarkdownContainer>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {t("Currently we cannot provide you with detailed information for your request.\n\nPlease **create a support case** to our support team, we will try our best to assist you with your need.")}
            </ReactMarkdown>
          </SamaritanMarkdownContainer>
        </SamaritanText>
      )}

      {response && (
        <SamaritanText>
          <SamaritanTextHeader>
            <SamaritanIcon />
            <ComponentXL textStyle={ComponentTextStyle.Bold}>{t("AI summarized answer")}</ComponentXL>
          </SamaritanTextHeader>
          <SamaritanMarkdownContainer>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {response}
            </ReactMarkdown>
          </SamaritanMarkdownContainer>
        </SamaritanText>
      )}

      {response && (
        <FeedbackContainer>
          {feedbackGiven ? (
            <FeedbackText>{t("Thank you for the feedback!")}</FeedbackText>
          ) : (
            <>
              <FeedbackText>{t("Was this helpful?")}</FeedbackText>
              <FeedbackButton onClick={() => handleFeedback(true)}>
                <SystemIcons.ThumbsUp />
              </FeedbackButton>
              <FeedbackButton onClick={() => handleFeedback(false)}>
                <SystemIcons.ThumbsDown />
              </FeedbackButton>
            </>
          )}
        </FeedbackContainer>
      )}
    </ResponseContainer>

      {response && !isStreaming && (
        <ComponentXXS style={{ color: COLORS.text_subtle, textAlign: 'center' }}>
          {t("AI-generated content can sometimes be inaccurate, please verify with our official documentation before use.")}
        </ComponentXXS>
      )}
    </>
  );
};

export default SamaritanResponse;
